import React, { useState, useEffect } from 'react';
import { signInWithPopup, GoogleAuthProvider } from 'firebase/auth';
import { auth, db } from '../firebase.config.js';
import { useAuthState } from 'react-firebase-hooks/auth';
import { collection, getDocs, doc, getDoc, setDoc, serverTimestamp } from 'firebase/firestore';
import { Code, AccountCircle, VpnKey } from '@mui/icons-material';
import './LoginOptions.css';
import logo from "../img/logo.png"

const LoginOptions = ({ onAuthSuccess }) => {
  const [selectedOption, setSelectedOption] = useState(null);
  const [agencyName, setAgencyName] = useState('');
  const [inviteCode, setInviteCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [currentUser] = useAuthState(auth);
  const [pendingInviteCode, setPendingInviteCode] = useState('');
  const [typewriterText, setTypewriterText] = useState('');
  const [autoLoggingIn, setAutoLoggingIn] = useState(false);

  // Typewriter effect
  useEffect(() => {
    const text = "Welcome to the future of artificial intelligence";
    let index = 0;
    
    const typeInterval = setInterval(() => {
      if (index < text.length) {
        setTypewriterText(text.slice(0, index + 1));
        index++;
      } else {
        clearInterval(typeInterval);
      }
    }, 80);

    return () => clearInterval(typeInterval);
  }, []);

  // Auto-join with pending code after Google auth
  React.useEffect(() => {
    // Check for pending code in localStorage first
    const storedCode = localStorage.getItem('pendingInviteCode');
    if (storedCode && !pendingInviteCode) {
      setPendingInviteCode(storedCode);
    }
    
    if (currentUser && (pendingInviteCode || storedCode) && !loading) {
      const codeToUse = pendingInviteCode || storedCode;
      console.log('Auto-joining with pending code:', codeToUse);
      
      const autoJoin = async () => {
        try {
          await joinWithCode(currentUser, codeToUse);
          setPendingInviteCode(''); // Clear state
          localStorage.removeItem('pendingInviteCode'); // Clear storage
        } catch (error) {
          console.error('Auto-join failed:', error);
          setPendingInviteCode(''); // Clear state
          localStorage.removeItem('pendingInviteCode'); // Clear storage
        }
      };
      autoJoin();
    }
  }, [currentUser, pendingInviteCode, loading]);

  const provider = new GoogleAuthProvider();
  provider.addScope('profile');
  provider.addScope('email');
  provider.setCustomParameters({
    prompt: 'select_account'
  });

  const handleGoogleAuth = async (mode) => {
    try {
      setLoading(true);
      setError('');
      
      let user = currentUser;
      
      // If not authenticated, sign in first
      if (!user) {
        // Store invite code before auth if we're in join mode
        if (mode === 'join' && inviteCode.trim()) {
          setPendingInviteCode(inviteCode.trim());
          localStorage.setItem('pendingInviteCode', inviteCode.trim());
        }
        
        const result = await signInWithPopup(auth, provider);
        user = result.user;
      }

      if (mode === 'create') {
        // Check if user already has an agency before creating
        const existingAgency = await checkUserExistingAgency(user.email);
        if (existingAgency) {
          setError('You already have an agency. Please use "Login to my agency" instead.');
          return;
        }
        await createNewAgency(user);
      } else if (mode === 'login') {
        await loginToExistingAgency(user);
      } else if (mode === 'join') {
        // If user is already authenticated, join immediately
        if (user === currentUser) {
          const codeToUse = inviteCode.trim();
          await joinWithCode(user, codeToUse);
        }
        // If not authenticated, the pending code is stored and useEffect will handle it
      }

    } catch (error) {
      console.error('Auth error:', error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const checkUserExistingAgency = async (email) => {
    try {
      console.log('Checking agencies for email:', email);
      const agenciesSnapshot = await getDocs(collection(db, 'agencies'));
      console.log('Found', agenciesSnapshot.docs.length, 'agencies');
      
      for (const agencyDoc of agenciesSnapshot.docs) {
        // Check agency settings for agency data
        const agencyInfoDoc = await getDoc(doc(db, 'agencies', agencyDoc.id, 'settings', 'agency_info'));
        if (!agencyInfoDoc.exists()) continue;
        
        const agencyData = agencyInfoDoc.data();
        if (!agencyData.name) continue;
        
        const userDocId = email.replace(/[^a-zA-Z0-9]/g, '_');
        const userDoc = await getDoc(doc(db, 'agencies', agencyDoc.id, 'authorized_users', userDocId));
        
        console.log(`Checking agency ${agencyDoc.id}, user exists:`, userDoc.exists());
        if (userDoc.exists()) {
          console.log('User data:', userDoc.data());
        }
        
        if (userDoc.exists() && userDoc.data().status === 'active') {
          const result = {
            key: agencyDoc.id,
            ...agencyData,
            userRole: userDoc.data().role
          };
          console.log('Found existing agency for user:', result);
          return result;
        }
      }
      
      console.log('No existing agency found for email:', email);
      return null;
    } catch (error) {
      console.error('Error checking existing agency:', error);
      return null;
    }
  };

  const createNewAgency = async (user) => {
    if (!agencyName.trim()) {
      setError('Please enter an agency name');
      return;
    }

    // Validate no spaces or special characters
    if (!/^[a-zA-Z0-9]+$/.test(agencyName.trim())) {
      setError('Agency name must contain only letters and numbers (no spaces or special characters)');
      return;
    }

    const agencyKey = agencyName.trim().toLowerCase();
    
    // Check if agency name already exists
    const existingAgency = await getDoc(doc(db, 'agencies', agencyKey, 'settings', 'agency_info'));
    if (existingAgency.exists()) {
      setError('An agency with this name already exists');
      return;
    }

    // Generate role-based invite codes
    const roleCodes = {
      Manager: generateInviteCode(),
      L1: generateInviteCode(),
      L2: generateInviteCode(),
      QA: generateInviteCode()
    };

    // Create agency info in settings subcollection
    await setDoc(doc(db, 'agencies', agencyKey, 'settings', 'agency_info'), {
      name: agencyName.trim(),
      key: agencyKey,
      createdAt: serverTimestamp(),
      createdBy: user.email,
      adminEmail: user.email,
      status: 'active',
      roleCodes: roleCodes
    });

    // Add user as admin
    const userDocId = user.email.replace(/[^a-zA-Z0-9]/g, '_');
    await setDoc(doc(db, 'agencies', agencyKey, 'authorized_users', userDocId), {
      email: user.email,
      role: 'admin',
      name: user.displayName || user.email,
      addedAt: serverTimestamp(),
      addedBy: user.email,
      status: 'active'
    });

    onAuthSuccess(agencyKey);
  };

  const loginToExistingAgency = async (user) => {
    try {
      // Check if user already has an agency
      const existingAgency = await checkUserExistingAgency(user.email);
      
      if (!existingAgency) {
        setError('No agency found for your account. Try creating a new agency or joining with an invitation code.');
        return;
      }

      console.log('Found existing agency:', existingAgency);
      onAuthSuccess(existingAgency.key);
    } catch (error) {
      console.error('Error in loginToExistingAgency:', error);
      setError('Error accessing your agency. Please try again.');
    }
  };

  // Auto-login after Google auth (from any screen)
  React.useEffect(() => {
    if (currentUser && !loading && !pendingInviteCode && !localStorage.getItem('pendingInviteCode') && !autoLoggingIn) {
      const autoLogin = async () => {
        try {
          setAutoLoggingIn(true);
          await loginToExistingAgency(currentUser);
        } catch (error) {
          console.error('Auto-login failed:', error);
          // Si l'auto-login échoue, on ne fait rien et l'utilisateur peut choisir manuellement
          setAutoLoggingIn(false);
        }
      };
      
      // Petit délai pour éviter les conflits avec d'autres useEffect
      setTimeout(autoLogin, 100);
    }
  }, [currentUser, loading, pendingInviteCode, autoLoggingIn]);

  const joinWithCode = async (user, codeToUse) => {
    const finalCode = codeToUse || inviteCode.trim();
    if (!finalCode) {
      setError('Please enter invitation code');
      return;
    }

    // Find agency with this invite code
    const agenciesSnapshot = await getDocs(collection(db, 'agencies'));
    let targetAgency = null;
    let targetRole = null;

    for (const agencyDoc of agenciesSnapshot.docs) {
      // Check agency settings for role codes
      const agencyInfoDoc = await getDoc(doc(db, 'agencies', agencyDoc.id, 'settings', 'agency_info'));
      if (!agencyInfoDoc.exists()) continue;
      
      const agencyData = agencyInfoDoc.data();
      if (!agencyData.roleCodes) continue;

      for (const [role, code] of Object.entries(agencyData.roleCodes)) {
        if (code === finalCode) {
          targetAgency = agencyDoc.id;
          targetRole = role;
          break;
        }
      }
      if (targetAgency) break;
    }

    if (!targetAgency) {
      setError('Invalid invitation code');
      return;
    }

    // Add user to agency with specified role
    const userDocId = user.email.replace(/[^a-zA-Z0-9]/g, '_');
    await setDoc(doc(db, 'agencies', targetAgency, 'authorized_users', userDocId), {
      email: user.email,
      role: targetRole,
      name: user.displayName || user.email,
      addedAt: serverTimestamp(),
      addedBy: 'invite-code',
      status: 'active'
    });

    console.log('User successfully added to agency:', targetAgency);
    onAuthSuccess(targetAgency);
  };

  const generateInviteCode = () => {
    return Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15);
  };

  if (selectedOption === 'create') {
    return (
      <div className="login-options">
        <div className="login-container">
          <h1>Create my agency</h1>
          <div className="form-group">
            <label>Agency name</label>
            <input
              type="text"
              value={agencyName}
              onChange={(e) => setAgencyName(e.target.value)}
              placeholder="Ex: myagency (no spaces)"
              className="form-input"
            />
          </div>
          {error && <div className="error-message">{error}</div>}
          <button 
            onClick={() => handleGoogleAuth('create')}
            disabled={loading || !agencyName.trim()}
            className="auth-button"
          >
            {loading ? 'Creating...' : (currentUser ? 'Create my agency' : 'Login with Google and create')}
          </button>
          <button onClick={() => setSelectedOption(null)} className="back-button">
            ← Back
          </button>
        </div>
      </div>
    );
  }

  if (selectedOption === 'login') {
    return (
      <div className="login-options">
        <div className="login-container">
          <h1>Access My Account</h1>
          <p>Sign in with Google to access your AI Factory workspace</p>
          {currentUser && (
            <p className="user-info">Logged in as: {currentUser.email}</p>
          )}
          {error && <div className="error-message">{error}</div>}
          <button 
            onClick={() => handleGoogleAuth('login')}
            disabled={loading}
            className="auth-button"
          >
            {loading ? 'Accessing your workspace...' : (currentUser ? 'Access My Workspace' : 'Sign in with Google')}
          </button>
          <button onClick={() => setSelectedOption(null)} className="back-button">
            ← Back
          </button>
        </div>
      </div>
    );
  }

  if (selectedOption === 'join') {
    return (
      <div className="login-options">
        <div className="login-container">

          <div className="form-group">
            <label>Invitation Code</label>
            <input
              type="text"
              value={inviteCode}
              onChange={(e) => setInviteCode(e.target.value)}
              placeholder="Enter your invitation code"
              className="form-input"
              autoFocus
            />
          </div>
          {error && <div className="error-message">{error}</div>}
          <button 
            onClick={() => handleGoogleAuth('join')}
            disabled={loading || !inviteCode.trim()}
            className="auth-button"
          >
            {loading ? 'Joining AI Factory...' : (currentUser ? 'Join AI Factory' : 'Sign in with Google & Join')}
          </button>
          <button onClick={() => setSelectedOption(null)} className="back-button">
            ← Back
          </button>
        </div>
      </div>
    );
  }

  // Show connecting loader if auto-logging in
  if (autoLoggingIn) {
    return (
      <div className="login-options">
        <div className="login-container">
          <img src={logo} alt="" />
          <div className="connecting-loader">
            <div className="loading-spinner"></div>
            <h2>Connecting...</h2>
            <p>Accessing your workspace</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="login-options">
      <div className="login-container">
        <img src={logo} alt="" />
        <p className="typewriter">{typewriterText}<span className="cursor">|</span></p>
        
        <div className="options-grid">
          {/* Create agency option - hidden for now */}
          {/* <div className="option-card" onClick={() => setSelectedOption('create')}>
            <div className="option-icon">
              <Business sx={{ fontSize: '2.8rem' }} />
            </div>
            <h3>Create my agency</h3>
            <p>Create a new agency and become administrator</p>
          </div> */}
          
          <div className="option-card featured" onClick={() => setSelectedOption('join')}>
            <div className="option-icon">
              <VpnKey sx={{ fontSize: '2.8rem' }} />
            </div>
            <h3>Join with Code</h3>
            <p>Enter your invitation code to get started</p>
          </div>
          
          <div className="option-card" onClick={() => setSelectedOption('login')}>
            <div className="option-icon">
              <AccountCircle sx={{ fontSize: '2.8rem' }} />
            </div>
            <h3>Access My Account</h3>
            <p>Sign in to your existing workspace</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginOptions;